// =====================================================================
// API アクセストークン管理 + fetch インターセプタ
//
// /api/check（CheckTempToken）成功時に発行される署名付きトークンを保持し、
// "/api/" 宛のすべての fetch に Authorization ヘッダーを自動付与する。
//
// Chatis の temp token はワンショット（照合で消滅する）ため、401 を受けても
// /api/check を呼び直すことはできない。復帰は Chatis の着地ルート /goto を
// 経由してログイン確認と temp token の再発行をしてもらう必要がある。
//
// トークンは sessionStorage（タブ単位）に保持する。リロード（F5）では
// /api/check を呼ばずにこのトークンで動作を継続する。
// =====================================================================

import { buildGotoUrl } from "./chatisUrls.ts";

/** アクセストークンの保存先（タブ単位。リロードに耐える） */
const TOKEN_KEY = "todo_api_token";

/** /goto 経由の再入場を 1 回に制限するためのフラグ */
const REENTER_KEY = "reentering";

type StoredToken = {
  token: string;
  honbuId: number;
  roomId: number;
};

let apiToken: string | null = null;
let interceptorInstalled = false;
let authFailureHandler: ((message: string) => void) | null = null;

const originalFetch = window.fetch.bind(window);

// ---------------------------------------------------------------------
// トークンの保持
// ---------------------------------------------------------------------

/** トークンの exp（UTC UnixTime 秒）を取り出す。読めない場合は null */
const readTokenExpiry = (token: string): number | null => {
  try {
    const payload = token.split(".")[0];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const fields = atob(padded).split("|");
    if (fields.length !== 3) return null;
    const exp = parseInt(fields[2], 10);
    return Number.isNaN(exp) ? null : exp;
  } catch {
    return null;
  }
};

/** exp を過ぎていないか判定する（exp が読めない場合は有効として扱う） */
const isTokenAlive = (token: string): boolean => {
  const exp = readTokenExpiry(token);
  if (exp === null) return true;
  return Math.floor(Date.now() / 1000) < exp;
};

/**
 * /api/check 成功時に呼び出し、トークンを sessionStorage とメモリに保持する。
 * honbuId / roomId を併せて保存し、別ルーム・別ユーザーで開き直した場合に
 * 前のトークンを使い回さないようにする。
 */
export const setApiAuth = (
  token: string,
  honbuId: number,
  roomId: number,
): void => {
  apiToken = token;
  try {
    const stored: StoredToken = { token, honbuId, roomId };
    sessionStorage.setItem(TOKEN_KEY, JSON.stringify(stored));
  } catch {
    // sessionStorage が使えない環境ではメモリ保持のみで動作する（リロードで切れる）
  }
};

/**
 * sessionStorage に保存された有効なトークンを取り出してメモリへ復元する。
 * honbuId / roomId が一致し、かつ期限内のものだけを有効とみなす。
 * 有効なトークンがある場合はリロードとみなし、/api/check を呼ばずに起動できる。
 */
export const restoreApiAuth = (honbuId: number, roomId: number): string | null => {
  try {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredToken;
    if (
      !stored?.token ||
      stored.honbuId !== honbuId ||
      stored.roomId !== roomId ||
      !isTokenAlive(stored.token)
    ) {
      clearApiAuth();
      return null;
    }
    apiToken = stored.token;
    return stored.token;
  } catch {
    return null;
  }
};

/** 保持しているトークンを破棄する */
export const clearApiAuth = (): void => {
  apiToken = null;
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // 何もしない
  }
};

// ---------------------------------------------------------------------
// /goto 経由の再入場（ループ防止付き）
// ---------------------------------------------------------------------

/** すでに /goto 経由で入り直した直後かどうか */
export const hasReentered = (): boolean => {
  try {
    return sessionStorage.getItem(REENTER_KEY) === "1";
  } catch {
    return false;
  }
};

/** 再入場フラグを立てる */
export const markReentering = (): void => {
  try {
    sessionStorage.setItem(REENTER_KEY, "1");
  } catch {
    // 何もしない
  }
};

/** 認証成功時に再入場フラグを消す */
export const clearReentering = (): void => {
  try {
    sessionStorage.removeItem(REENTER_KEY);
  } catch {
    // 何もしない
  }
};

/**
 * Chatis の iframe に埋め込まれて表示されているか。
 * クロスオリジンで window.top を参照できない場合も埋め込みとみなす。
 */
const isEmbedded = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

/**
 * 現在の URL のクエリを引き継いで /goto へリダイレクトする。
 * shortcut=1 は現在の URL に付いていた場合のみ引き継ぐ
 * （通常は Chatis の iframe 内で動作しており、無条件に付けると
 *   iframe 内にショートカットヘッダーが表示されてしまう）。
 *
 * @returns リダイレクトを開始した場合 true。中止した場合 false
 */
export const reenterViaChatis = (): boolean => {
  // iframe 内でこれをやると、/goto がメイン画面へ遷移するため
  // TODO の iframe の中に Chatis が入れ子で開いてしまう。
  // 埋め込み時はバウンスせず、呼び出し側にエラー表示を任せる
  // （利用者はダイアログを閉じて開き直せば新しい temp token で入り直せる）。
  if (isEmbedded()) return false;

  if (hasReentered()) {
    // 2 回目 → これ以上バウンスさせない
    clearReentering();
    return false;
  }
  markReentering();

  const query = new URLSearchParams(window.location.search);
  const roomId = query.get("roomid") ?? "0";
  const color = query.get("color");
  const mode = query.get("mode");

  window.location.replace(
    buildGotoUrl({
      app: "todo",
      roomId,
      color: color ?? undefined,
      isDarkMode: mode === null ? undefined : mode === "dark",
      shortcut: query.get("shortcut") === "1",
    }),
  );
  return true;
};

/** 再入場もできなかった場合にユーザーへ通知するハンドラを登録する */
export const setAuthFailureHandler = (
  handler: ((message: string) => void) | null,
): void => {
  authFailureHandler = handler;
};

// ---------------------------------------------------------------------
// fetch インターセプタ
// ---------------------------------------------------------------------

/** リクエスト URL を文字列として取り出す */
const getUrl = (input: RequestInfo | URL): string => {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
};

/**
 * window.fetch を差し替えて Authorization ヘッダーを自動付与する（App 起動時に 1 回呼ぶ）。
 * 401 の場合はトークンを破棄し、/goto 経由で 1 回だけ入り直す。
 */
export const installApiAuthInterceptor = (): void => {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const url = getUrl(input);

    // API 以外・認証チェック自体・トークン未取得はそのまま通す
    if (!url.startsWith("/api/") || url.startsWith("/api/check") || !apiToken) {
      return originalFetch(input, init);
    }

    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    headers.set("Authorization", `Bearer ${apiToken}`);
    const res = await originalFetch(input, { ...init, headers });

    // トークン失効時は Chatis 経由で入り直す（temp token がワンショットのため
    // /api/check の再実行では復帰できない）
    if (res.status === 401) {
      clearApiAuth();
      if (!reenterViaChatis()) {
        authFailureHandler?.(
          "セッションが切れました。Chatis の TODO ボタンから開き直してください。",
        );
      }
    }
    return res;
  };
};
